import React from 'react';
import { Button, Steps } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import useScreenWidth from '../../../../../hooks/useScreenSize';
import './eventsDpStepper.less';

export enum EventsDpStep {
  EVENT_SELECTION = 0,
  NIGHTS = 1,
  PACKAGE_SELECTION = 2,
  DETAILS = 3,
}

/**
 * URL marker that pins the flow to the Nights step even though the package
 * params (from/to) are already applied — set by back-navigation, deleted when
 * the Nights step's search runs.
 */
export const DP_STEP_PARAM = 'dpStep';
export const DP_STEP_NIGHTS = 'nights';

interface EventsDpStepperProps {
  current: EventsDpStep;
  // Called only for steps before the current one
  onStepClick?: (step: EventsDpStep) => void;
  // Set when the back button is rendered elsewhere (e.g. SelectedEventBanner
  // renders it in its own header) so it is not duplicated here.
  hideBackButton?: boolean;
  /**
   * Drops the global `content-wrapper` class. Set when the stepper is nested in
   * a parent that already constrains width: content-wrapper is
   * `width: 90vw; max-width: 1100px`, so nesting one inside another makes the
   * inner element 90vw of the *viewport* and it overflows its own parent.
   */
  fitToParent?: boolean;
}

// Step titles in enum order — indexable by EventsDpStep.
const useStepTitles = (): string[] => {
  const { searchDatePicker: t } = useSelector(
    (data: RootState) => data.config.translations
  );
  return [
    t.stepEventSelection,
    t.stepNights,
    t.stepPackageSelection,
    t.stepDetailsSelection,
  ];
};

/**
 * "Back to <previous step>" button. Extracted so consumers that lay the stepper
 * out themselves — SelectedEventBanner puts it in the banner header — reuse the
 * same label/step logic instead of re-deriving the step titles.
 */
export const EventsDpStepperBackButton: React.FC<{
  current: EventsDpStep;
  onStepClick?: (step: EventsDpStep) => void;
  className?: string;
}> = ({ current, onStepClick, className }) => {
  const { searchDatePicker: t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const stepTitles = useStepTitles();

  if (current <= EventsDpStep.EVENT_SELECTION) {
    return null;
  }

  return (
    <Button
      className={`events-dp-stepper-back${className ? ` ${className}` : ''}`}
      onClick={() => onStepClick?.(current - 1)}
    >
      {t.stepBack?.replace('{{step}}', stepTitles[current - 1])}
    </Button>
  );
};

/**
 * Phones: antd's Steps fall back to a ~200px vertical list there. Instead an
 * app-wizard header —
 *   Step 2 of 4
 *   Nights                       ▬▬▬ ▬▬▬ ─── ───
 * the segments of finished steps go back to that step, like the desktop
 * step titles do.
 */
const EventsDpStepperCompact: React.FC<{
  current: EventsDpStep;
  stepTitles: string[];
  onStepClick?: (step: EventsDpStep) => void;
}> = ({ current, stepTitles, onStepClick }) => {
  const { searchDatePicker: t } = useSelector(
    (data: RootState) => data.config.translations
  );

  return (
    <div className="events-dp-stepper__compact">
      <div className="events-dp-stepper__compact-text">
        <span className="events-dp-stepper__compact-count">
          {t.stepOfTotal
            ?.replace('{{current}}', String(current + 1))
            ?.replace('{{total}}', String(stepTitles.length))}
        </span>
        <span className="events-dp-stepper__compact-title">
          {stepTitles[current]}
        </span>
      </div>
      <ol className="events-dp-stepper__segments">
        {stepTitles.map((title, step) => (
          <li key={step}>
            <button
              type="button"
              className={`events-dp-stepper__segment${
                step < current ? ' is-done' : ''
              }${step === current ? ' is-current' : ''}`}
              aria-label={title}
              aria-current={step === current ? 'step' : undefined}
              disabled={step >= current}
              onClick={() => onStepClick?.(step)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
};

const EventsDpStepper: React.FC<EventsDpStepperProps> = ({
  current,
  onStepClick,
  hideBackButton = false,
  fitToParent = false,
}) => {
  const stepTitles = useStepTitles();
  const { isMobileWidth } = useScreenWidth();

  return (
    <div
      className={`events-dp-stepper${fitToParent ? '' : ' content-wrapper'}`}
    >
      {!hideBackButton && (
        <EventsDpStepperBackButton
          current={current}
          onStepClick={onStepClick}
        />
      )}
      {isMobileWidth ? (
        <EventsDpStepperCompact
          current={current}
          stepTitles={stepTitles}
          onStepClick={onStepClick}
        />
      ) : (
      <Steps
        current={current}
        size="small"
        onChange={(step) => {
          if (step < current) {
            onStepClick?.(step);
          }
        }}
        items={[
          { title: stepTitles[EventsDpStep.EVENT_SELECTION] },
          {
            title: stepTitles[EventsDpStep.NIGHTS],
            disabled: current < EventsDpStep.NIGHTS,
          },
          {
            title: stepTitles[EventsDpStep.PACKAGE_SELECTION],
            disabled: current < EventsDpStep.PACKAGE_SELECTION,
          },
          {
            title: stepTitles[EventsDpStep.DETAILS],
            disabled: current < EventsDpStep.DETAILS,
          },
        ]}
      />
      )}
    </div>
  );
};

export default EventsDpStepper;
